import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import { type MouseEvent, type ReactNode, useState } from 'react';
import { ControlButton } from '../filters/ControlButton.js';
import { FILTER_MENU_SLOT_PROPS } from '../filters/menuStyles.js';
import { FieldHeader } from './FieldHeader.js';
import { FIELD_WIDTH } from './fieldLayout.js';
import type { FieldOptionsResult } from './useFieldOptions.js';

interface LookupControlProps {
  label: string;
  tooltip: string | null;
  chosen: string | undefined;
  resolved: FieldOptionsResult;
  hideLabel?: boolean;
  children: (close: () => void) => ReactNode;
}

// The lead list may be empty while the rest still sits behind "See more".
function isEmpty(resolved: FieldOptionsResult): boolean {
  return resolved.options.length === 0 && (resolved.more?.length ?? 0) === 0;
}

export function summaryOf(chosen: string | undefined, resolved: FieldOptionsResult): string {
  if (chosen !== undefined && chosen !== '') return chosen;
  if (resolved.unsupported) return 'Not available yet';
  if (resolved.loading) return 'Loading…';
  return isEmpty(resolved) ? 'None in scope' : 'Any';
}

export function LookupControl({
  label,
  tooltip,
  chosen,
  resolved,
  hideLabel = false,
  children,
}: LookupControlProps): ReactNode {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <Stack sx={{ gap: 1.5 }}>
      {hideLabel ? null : <FieldHeader label={label} tooltip={tooltip} />}
      <ControlButton
        label=""
        value={summaryOf(chosen, resolved)}
        placeholder={chosen === undefined || chosen === ''}
        open={anchor !== null}
        disabled={isEmpty(resolved)}
        onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget)}
        fixedWidth
        width={FIELD_WIDTH}
      />
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        slotProps={FILTER_MENU_SLOT_PROPS}
      >
        {children(() => setAnchor(null))}
      </Menu>
    </Stack>
  );
}
